<template>
    <teleport to="body">
        <transition name="modal-fade">
            <div
                v-if="modelValue"
                class="modal-overlay"
                :class="{ 'dark-mode': darkMode }"
                @click="handleOutsideClick"
                ref="modalOverlayRef"
            >
                <div
                    class="modal-container"
                    :class="{
                        'fullscreen-mobile': fullscreenMobile,
                        'dark-mode': darkMode,
                    }"
                    :style="{
                        backgroundColor: darkMode
                            ? 'transparent'
                            : backgroundColor,
                        color: darkMode ? '#ffffff' : fontColor,
                    }"
                    @click.stop
                >
                    <div class="modal-header">
                        <div class="header-left">
                            <slot name="header"></slot>
                        </div>
                        <button
                            v-if="closable"
                            class="close-button"
                            :class="{ 'dark-mode': darkMode }"
                            :style="{ color: darkMode ? '#ffffff' : fontColor }"
                            @click="closeModal"
                            aria-label="Close"
                        >
                            <!-- Use PrimeIcons 'pi pi-times' instead of close.svg -->
                            <i class="pi pi-times" aria-hidden="true"></i>
                        </button>
                    </div>
                    <div
                        class="modal-content"
                        :style="{ padding: contentPadding }"
                    >
                        <slot></slot>
                    </div>
                </div>
            </div>
        </transition>
    </teleport>
</template>

<script setup>
import { ref, watch, onMounted, onBeforeUnmount } from 'vue'

const props = defineProps({
    modelValue: {
        type: Boolean,
        default: false,
    },
    closable: {
        type: Boolean,
        default: true,
    },
    contentPadding: {
        type: String,
        default: '20px',
    },
    closeOnClickOutside: {
        type: Boolean,
        default: true,
    },
    closeOnEsc: {
        type: Boolean,
        default: true,
    },
    backgroundColor: {
        type: String,
        default: '#ffffff',
    },
    fontColor: {
        type: String,
        default: '#333',
    },
    fullscreenMobile: {
        type: Boolean,
        default: false,
    },
    darkMode: {
        type: Boolean,
        default: false,
    },
})

const emit = defineEmits(['update:modelValue', 'close'])
const modalOverlayRef = ref(null)

// Handle escape key
const handleKeyDown = (e) => {
    if (e.key === 'Escape' && props.closeOnEsc && props.modelValue) {
        closeModal()
    }
}

// Close modal
const closeModal = () => {
    emit('update:modelValue', false)
    emit('close')
}

// Handle outside click
const handleOutsideClick = (e) => {
    if (props.closeOnClickOutside && e.target === modalOverlayRef.value) {
        closeModal()
    }
}

// Lock body scroll when modal is open
watch(
    () => props.modelValue,
    (newValue) => {
        if (newValue) {
            document.body.style.overflow = 'hidden'
        } else {
            document.body.style.overflow = ''
        }
    }
)

onMounted(() => {
    window.addEventListener('keydown', handleKeyDown)
})

onBeforeUnmount(() => {
    window.removeEventListener('keydown', handleKeyDown)
    // Ensure body overflow is restored if component is unmounted while open
    if (props.modelValue) {
        document.body.style.overflow = ''
    }
})
</script>

<style scoped>
/* Reset for modal container to avoid inheritance */
/* .modal-container * {
  all: revert;
} */

.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.7);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 10000;
}

.modal-container {
    position: relative;
    border-radius: 16px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    overflow: hidden;
}

.modal-header {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    display: flex;
    justify-content: flex-end;
    gap: 20px;
    align-items: center;
    padding: 22px 23px 0;
    z-index: 10001;
    pointer-events: none;
}

.header-left {
    pointer-events: auto;
}

.close-button {
    background: none;
    border: none;
    cursor: pointer;
    padding: 5px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    pointer-events: auto;
}

.close-button:hover {
    background-color: rgba(0, 0, 0, 0.05);
}

.modal-content {
    overflow-y: auto;
    padding: 20px;
}

/* Responsive design */
@media (max-width: 767px) {
    .modal-container {
        width: calc(100% - 32px);
        max-width: 400px;
        max-height: calc(100% - 32px);
        height: auto;
        margin: 16px;
        border-radius: 16px;
        display: block;
        position: relative;
    }

    .modal-container.fullscreen-mobile {
        width: 100%;
        height: 100%;
        max-width: none;
        max-height: none;
        margin: 0;
        border-radius: 0;
        position: fixed;
        top: 0;
        left: 0;
        border-radius: 0 !important;
    }

    .modal-content {
        padding: 16px;
        max-height: none;
        overflow: auto;
    }

    .modal-container.fullscreen-mobile .modal-content {
        height: 100%;
        padding: 20px;
        display: flex;
        flex-direction: column;
    }

    .modal-header {
        padding: 16px 16px 0;
    }

    .modal-container.fullscreen-mobile .modal-header {
        padding: 20px 20px 0;
    }
}

@media (min-width: 768px) {
    .modal-container {
        min-width: 360px;
        min-height: 180px;
        max-width: 800px;
        max-height: 85vh;
    }

    .modal-content {
        max-height: calc(85vh - 40px); /* 40px for padding */
    }

    .modal-container.dark-mode {
        background-color: transparent !important;
        background: transparent !important;
        box-shadow: none !important;
    }

    .modal-overlay.dark-mode .modal-header {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        padding: 24px 24px 0;
    }
}

/* Transitions */
.modal-fade-enter-active,
.modal-fade-leave-active {
    transition: opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
    opacity: 0;
}

.close-button img {
    filter: invert(1);
}

/* Dark mode styles */
.modal-overlay.dark-mode {
    background-color: rgba(0, 0, 0, 0.65);
    backdrop-filter: blur(64px);
    -webkit-backdrop-filter: blur(64px);
}

.modal-container.dark-mode {
    /* No background on container - overlay handles it */
    background: transparent;
    border-radius: 16px;
    box-shadow: none;
}

.close-button.dark-mode {
    color: #ffffff;
}

.close-button.dark-mode:hover {
    background-color: rgba(255, 255, 255, 0.15);
}

.close-button.dark-mode i {
    color: #ffffff;
    font-size: 20px;
}
</style>
